import axios from '../globalAxios';
import { AxiosResponse, CancelToken } from 'axios';
import {
    isZipJobCancelled,
    waitForAsyncZipJob,
    ZipPollOptions as SharedZipPollOptions,
} from 'molo-shared';
import { appConfig } from '../../config';

const baseUrl = appConfig.api.baseUrl;

export interface ZipPollOptions extends SharedZipPollOptions {
    cancelToken?: CancelToken;
}

function isZipRequestCancelled(error: unknown) {
    return axios.isCancel(error) || isZipJobCancelled(error);
}

function notifyPreparing(onProgress?: (message: string) => void) {
    if (onProgress) {
        onProgress('Preparing your download…');
    }
}

async function waitForPackZipJobIfNeeded(
    response: AxiosResponse,
    onProgress?: (message: string) => void,
    pollOptions?: ZipPollOptions
): Promise<AxiosResponse> {
    const cancelToken = pollOptions && pollOptions.cancelToken;

    const result = await waitForAsyncZipJob(response && response.data, {
        pollJob: async (jobId) => {
            const pollUrl = baseUrl + `Order/GetPackZipJobStatus/${jobId}`;
            const statusResponse = await axios.get(
                pollUrl,
                cancelToken ? { cancelToken } : undefined
            );
            return statusResponse.data;
        },
        onProgress,
        pollOptions,
    });

    if (!result) {
        return response;
    }

    return {
        ...response,
        data: {
            ...response.data,
            location: result.downloadUrl,
            message: result.message || 'Ready for download',
            async: false,
            added: result.added,
            failed: result.failed,
            errors: result.errors,
        },
    };
}

export function mapFileApiZipResponse(response: AxiosResponse) {
    if (!response.data || !response.data.location) {
        throw new Error('Download could not be prepared.');
    }

    return {
        downloadUrl: response.data.location as string,
        added: response.data.added,
        failed: response.data.failed,
        errors: response.data.errors,
    };
}

export { isZipRequestCancelled };

export default class FileApi {
    static cancelPackZipJob = async (jobId: string) => {
        const url = baseUrl + `Order/CancelPackZipJob/${jobId}`;
        await axios.delete(url);
    };

    static createOrderImageZip = async (
        orderId: number,
        onProgress?: (message: string) => void,
        pollOptions?: ZipPollOptions
    ) => {
        notifyPreparing(onProgress);
        const url = baseUrl + 'Order/CreateOrderImageZip/' + orderId;
        const response = await axios.post(
            url,
            null,
            pollOptions && pollOptions.cancelToken
                ? { cancelToken: pollOptions.cancelToken }
                : undefined
        );
        return await waitForPackZipJobIfNeeded(response, onProgress, pollOptions);
    };

    static downloadZipFile = async (location: string) => {
        return await axios.get(location);
    };

    static createCustomerDropZip = async (
        customerno: string,
        deliverycode: string,
        seasoncode: string,
        onProgress?: (message: string) => void,
        pollOptions?: ZipPollOptions
    ) => {
        notifyPreparing(onProgress);
        const url = baseUrl + `Order/CreateCustomerDropZip/${customerno}/${deliverycode}/${seasoncode}`;
        const response = await axios.post(url);
        return await waitForPackZipJobIfNeeded(response, onProgress, pollOptions);
    };

    static createCustomerUnshippedItemsZip = async (
        sonumber: string,
        deliverycode: string,
        onProgress?: (message: string) => void,
        pollOptions?: ZipPollOptions
    ) => {
        notifyPreparing(onProgress);
        const url = baseUrl + `Order/CreateNavOrderUnshippedItemsZip/${sonumber}?deliverycode=${deliverycode}`;
        const response = await axios.post(url);
        return await waitForPackZipJobIfNeeded(response, onProgress, pollOptions);
    };

    static createCustomerBSLItemsZip = async (
        bsl: string,
        onProgress?: (message: string) => void,
        pollOptions?: ZipPollOptions
    ) => {
        notifyPreparing(onProgress);
        const url = baseUrl + `Order/CreateNavOrderBSLZip/${bsl}`;
        const response = await axios.post(url);
        return await waitForPackZipJobIfNeeded(response, onProgress, pollOptions);
    };

    static createCustomerMultipleDropZip = async (
        customerno: string,
        data: any[],
        onProgress?: (message: string) => void,
        pollOptions?: ZipPollOptions
    ) => {
        notifyPreparing(onProgress);
        const url = baseUrl + 'Order/CreateCustomerMultipleDropZip';
        const response = await axios.post(url, data, {
            params: {
                customerno,
            },
        });
        return await waitForPackZipJobIfNeeded(response, onProgress, pollOptions);
    };
}
